import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Col, Input, message, Popconfirm, Row, Select, Space, Table, Tag, Tooltip} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {monthOptions, statusOptions} from "../../_helpers/variables";
import CreateStatementModal, {salaryStatementsAPI} from "./CreateStatementModal";
import ViewStatementModal from "./ViewStatementModal";
import EditStatementModal from "./EditStatementModal";
import style from '../styles/expenses.salary.statements.table.module.css'
import {Amount} from "./subsubcomponents/Amount";

const {Option} = Select;


const SalaryStatementsTable = ({module_v}) => {
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedStatement, setSelectedStatement] = useState(null);

    // Загрузка ведомостей при изменении фильтров
    useEffect(() => {
        fetchStatements();
    }, [selectedYear, selectedMonth]);

    const fetchStatements = useCallback(async () => {
        try {
            setLoading(true);
            const response = await salaryStatementsAPI.getStatements({
                year: selectedYear,
                month: selectedMonth
            });

            if (response.success) {
                const statements = response.statements || [];
                const formattedData = statements.map(statement => ({
                    id: statement.id,
                    date: statement.date,
                    name: statement.name,
                    description: statement.description,
                    employeeCount: statement.employee_count,
                    totalAmount: statement.total_amount,
                    cashAmount: statement.cash_amount,
                    nonCashWithoutVAT: statement.non_cash_without_vat,
                    nonCashWithVAT: statement.non_cash_with_vat,
                    status: statement.status,
                    staffIds: statement.staff_ids,
                    staffDetails: statement.staff_details,
                    createdAt: statement.created_at,
                    updatedAt: statement.updated_at,
                    rawData: statement // сохраняем сырые данные для модальных окон
                }));
                setData(formattedData);
            } else {
                message.error(response.message || 'Ошибка при загрузке ведомостей');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            message.error('Ошибка подключения к серверу');
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth]);

    // Генерация списка годов
    const yearOptions = useMemo(() => {
        const currentYear = dayjs().year();
        const years = [];
        for (let year = 2024; year <= currentYear + 1; year++) {
            years.push(year);
        }
        return years;
    }, []);

    // Фильтрация данных
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const matchesSearch = searchText === '' ||
                item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchText.toLowerCase());

            const matchesStatus = statusFilter === 'all' || item.status === statusFilter;

            return matchesSearch && matchesStatus;
        }).sort((a, b) => a.date - b.date);
    }, [data, searchText, statusFilter]);

    const handleSearch = (value) => setSearchText(value);
    const handleStatusChange = (value) => setStatusFilter(value);
    const handleYearChange = (value) => setSelectedYear(value);
    const handleMonthChange = (value) => setSelectedMonth(value);

    const handleOpenCreateModal = () => {
        setIsCreateModalVisible(true);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalVisible(false);
    };

    const handleSubmitForm = async (formData) => {
        fetchStatements();
    };

    const handleResetFilters = () => {
        setSearchText('');
        setStatusFilter('all');
        setSelectedYear(dayjs().year());
        setSelectedMonth(dayjs().month() + 1);
    };

    const handleView = (record) => {
        setSelectedStatement(record.rawData);
        setIsViewModalVisible(true);
    };

    const handleEdit = (record) => {
        setSelectedStatement(record.rawData);
        setIsEditModalVisible(true);
    };

    const handleDelete = async (record) => {
        try {
            const result = await salaryStatementsAPI.deleteStatement(record.id);
            if (result.success) {
                message.success('Ведомость удалена');
                fetchStatements();
            } else {
                message.error(result.message || 'Ошибка удаления');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            message.error('Ошибка удаления ведомости');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 50,
            align: 'center',
        },
        {
            title: 'Дата',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
            render: (date) => dayjs(date).format('MMMM YYYY'),
        },
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            width: 250,
            sorter: (a, b) => a.name?.localeCompare(b.name),
            render: (name, record) => (
                <Tooltip title={record.description} placement="topLeft">
                    <span style={{cursor: 'help'}}>
                        {name}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Сотрудники',
            dataIndex: 'employeeCount',
            key: 'employeeCount',
            align: 'center',
            width: 100,
            sorter: (a, b) => a.employeeCount - b.employeeCount,
            render: (count, record) => (
                <Tag color="blue">{count} </Tag>
            ),
        },
        {
            title: 'Сумма (€)',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 100,
            sorter: (a, b) => a.totalAmount - b.totalAmount,
            render: (amount, record) => (
                <Tooltip title={<Amount props={record}/>} placement="topLeft">
                <span style={{fontWeight: 500}}>
                    {amount?.toLocaleString('ru-RU')}
                </span>
                </Tooltip>
            ),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => {
                const option = statusOptions.find(opt => opt.value === status);
                return <Tag color={option?.color}>{option?.label}</Tag>;
            },
        },
        {
            title: 'Добавлен',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 100,
            sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
            render: (date) => <span className={style.dates__text}>{dayjs(date).format('DD.MM.YYYY HH:mm')}</span>,
        },
        {
            title: 'Обновлен',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 100,
            sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
            render: (date) => <span className={style.dates__text}>{dayjs(date).format('DD.MM.YYYY HH:mm')}</span>,
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Space orientation="horizontal">
                    <Tooltip title="Просмотреть">
                        <Button
                            type="text"
                            icon={<EyeOutlined/>}
                            onClick={() => handleView(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Редактировать">
                        <Button
                            type="text"
                            icon={<EditOutlined/>}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Удалить ведомость?"
                        description="Вы уверены, что хотите удалить эту ведомость?"
                        onConfirm={() => handleDelete(record)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Tooltip title="Удалить">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    console.log(filteredData)

    return (
        <div style={{padding: '24px'}}>
            <Card
                /* title="Зарплатные ведомости"*/
                extra={
                    <Space orientation="horizontal">
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleOpenCreateModal}
                        >
                            Новая ведомость
                        </Button>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={handleResetFilters}
                        >
                            Сбросить фильтры
                        </Button>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={fetchStatements}
                        >
                            Обновить
                        </Button>
                    </Space>
                }
            >
                <Space orientation="vertical" size="middle" style={{width: '100%', marginBottom: '16px'}}>
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={8}>
                            <Input
                                placeholder="Поиск по названию и описанию..."
                                prefix={<SearchOutlined/>}
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Select
                                style={{width: '100%'}}
                                placeholder="Статус"
                                value={statusFilter}
                                onChange={handleStatusChange}
                                allowClear
                            >
                                <Option value="all">Все статусы</Option>
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={6}>
                            <Select
                                style={{width: '100%'}}
                                placeholder="Год"
                                value={selectedYear}
                                onChange={handleYearChange}
                            >
                                <Option key={null} value={null}>
                                    {'Все годы'}
                                </Option>
                                {yearOptions.map(year => (
                                    <Option key={year} value={year}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={6}>
                            <Select
                                style={{width: '100%'}}
                                placeholder="Месяц"
                                value={selectedMonth}
                                onChange={handleMonthChange}
                            >
                                <Option key={null} value={null}>
                                    {'Все месяцы'}
                                </Option>
                                {monthOptions.map(month => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>

                    <Row gutter={[8, 8]}>
                        <Col>
                            <Tag color="processing">Всего записей: {data.length}</Tag>
                        </Col>
                        <Col>
                            <Tag color="success">Найдено: {filteredData.length}</Tag>
                        </Col>
                        <Col>
                            <Tag color="purple">
                                Общая
                                сумма: {filteredData.reduce((sum, item) => sum + (item.totalAmount || 0), 0).toLocaleString('ru-RU')} €
                            </Tag>
                        </Col>
                    </Row>
                </Space>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    size="small"
                    bordered
                    loading={loading}
                    pagination={false}
                    // pagination={{
                    //     pageSize: 10,
                    //     showSizeChanger: true,
                    //     showQuickJumper: true,
                    //     showTotal: (total, range) =>
                    //         `${range[0]}-${range[1]} из ${total} записей`,
                    // }}
                    scroll={{x: 1200}}
                />
            </Card>

            {/* Модальные окна */}
            <CreateStatementModal
                visible={isCreateModalVisible}
                onCancel={handleCloseCreateModal}
                onSubmit={handleSubmitForm}
                refreshList={fetchStatements}
            />

            <ViewStatementModal
                visible={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                statementData={selectedStatement}
                module_v={module_v}
            />

            <EditStatementModal
                visible={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                onSubmit={() => {
                    setIsEditModalVisible(false);
                    fetchStatements();
                }}
                refreshList={fetchStatements}
                statementData={selectedStatement}
            />
        </div>
    );
};

export default SalaryStatementsTable;